// Print a single expense voucher on A4 — professional accounting format.
import { API_BASE } from "@/lib/api";

const resolveImg = (u) => {
  if (!u) return "";
  if (u.startsWith("http")) return u;
  return API_BASE.replace(/\/api$/, "") + u;
};

const fmt = (n) =>
  Number(n || 0).toLocaleString("en", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const escape = (s) => {
  if (s == null) return "";
  return String(s)
    .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;").replace(/'/g, "&#39;");
};

// Number → spoken amount (very basic English/Arabic helper). Optional decoration.
const amountInWords = (n, isAR) => {
  // Lightweight presentation only — many salons need the digit, not the words.
  const value = Number(n || 0).toFixed(2);
  return isAR ? `(${value} ر.ق فقط لا غير)` : `(${value} QAR only)`;
};

export function printExpense(expense, account, template, lang = "ar") {
  const isAR = lang === "ar";
  const dir = isAR ? "rtl" : "ltr";
  const t = (en, ar) => (isAR ? ar : en);
  const primary = template?.primary_color || "#D4AF64";

  const dateStr = (() => {
    try { return new Date(expense.created_at || expense.date).toLocaleString(isAR ? "ar-EG" : "en-US"); }
    catch { return expense.date || ""; }
  })();

  const html = `
<!DOCTYPE html>
<html lang="${isAR ? "ar" : "en"}" dir="${dir}">
<head>
<meta charset="UTF-8" />
<title>${escape(expense.expense_number || expense.expense_id || "")}</title>
<style>
  @page { size: A4; margin: 14mm; }
  * { box-sizing: border-box; }
  html, body {
    margin: 0; padding: 0;
    font-family: ${isAR ? "'IBM Plex Sans Arabic', sans-serif" : "'Inter', -apple-system, sans-serif"};
    color: #111; background: #fff; font-size: 11pt;
  }
  .page { width: 100%; max-width: 182mm; margin: 0 auto; padding: 8mm 0; }
  .header { display: flex; justify-content: space-between; align-items: flex-start; gap: 16px; padding-bottom: 12px; border-bottom: 2px solid ${primary}; }
  .brand { display: flex; gap: 12px; align-items: center; }
  .logo { width: 70px; height: 70px; object-fit: contain; }
  .biz h1 { margin: 0 0 4px 0; font-size: 20pt; color: ${primary}; }
  .biz .meta { font-size: 9pt; color: #555; line-height: 1.5; }
  .doc-info { text-align: ${isAR ? "left" : "right"}; }
  .doc-info .doc-title { font-size: 16pt; color: ${primary}; font-weight: 700; margin-bottom: 6px; }
  .doc-info .row { font-size: 10pt; color: #333; line-height: 1.6; }
  .doc-info .label { color: #777; margin-${isAR ? "left" : "right"}: 6px; }

  .voucher { margin-top: 24px; padding: 22px; border: 2px solid ${primary}; border-radius: 8px; background: #fffaf0; }
  .voucher .amount-row { display: flex; justify-content: space-between; align-items: center; padding-bottom: 18px; border-bottom: 1px dashed #d1c69f; margin-bottom: 18px; }
  .voucher .amount-row .lbl { font-size: 10pt; color: #888; text-transform: uppercase; letter-spacing: .5px; }
  .voucher .amount-row .amt { font-size: 36pt; font-weight: 700; color: ${primary}; line-height: 1; font-variant-numeric: tabular-nums; }
  .voucher .amount-row .words { font-size: 10pt; color: #777; margin-top: 6px; font-style: italic; }
  .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 14px 22px; }
  .grid .cell .k { font-size: 9pt; color: #888; text-transform: uppercase; letter-spacing: .5px; margin-bottom: 4px; }
  .grid .cell .v { font-size: 12pt; color: #111; font-weight: 500; }

  .desc-box { margin-top: 20px; padding: 14px 16px; background: #fafafa; border: 1px dashed #ccc; border-radius: 4px; }
  .desc-box h3 { margin: 0 0 6px 0; font-size: 9pt; color: #666; text-transform: uppercase; letter-spacing: 0.5px; }
  .desc-box .v { font-size: 11pt; color: #111; white-space: pre-line; line-height: 1.6; }

  .sig-grid { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 30px; margin-top: 50px; }
  .sig-box { border-top: 1px solid #888; padding-top: 6px; font-size: 10pt; color: #444; text-align: center; }

  .footer { margin-top: 24px; padding-top: 14px; border-top: 1px dashed #aaa; font-size: 9pt; color: #888; text-align: center; }

  .actions { margin-top: 12px; text-align: center; padding-top: 14px; }
  .actions button { background: ${primary}; color: #fff; border: 0; padding: 10px 20px; border-radius: 6px; cursor: pointer; font-size: 11pt; }

  @media print {
    .actions { display: none; }
    body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  }
</style>
</head>
<body>
  <div class="page">
    <div class="header">
      <div class="brand">
        ${template?.logo_url ? `<img class="logo" src="${escape(resolveImg(template.logo_url))}" alt="" />` : ""}
        <div class="biz">
          <h1>${escape(isAR ? template?.business_name_ar : template?.business_name_en) || ""}</h1>
          <div class="meta">
            ${template?.phone ? `<div>${t("Phone", "هاتف")}: ${escape(template.phone)}</div>` : ""}
            ${template?.email ? `<div>${escape(template.email)}</div>` : ""}
            ${template?.tax_number ? `<div>${t("VAT", "الرقم الضريبي")}: ${escape(template.tax_number)}</div>` : ""}
            ${template?.cr_number ? `<div>${t("CR", "س.ت")}: ${escape(template.cr_number)}</div>` : ""}
          </div>
        </div>
      </div>
      <div class="doc-info">
        <div class="doc-title">${t("Expense Voucher", "سند صرف")}</div>
        <div class="row"><span class="label">${t("Voucher #", "رقم السند")}:</span> <strong>${escape(expense.expense_number || (expense.expense_id || "").slice(-8).toUpperCase())}</strong></div>
        <div class="row"><span class="label">${t("Date", "التاريخ")}:</span> ${escape(expense.date || dateStr)}</div>
        <div class="row"><span class="label">${t("Created", "أنشئ")}:</span> ${escape(dateStr)}</div>
      </div>
    </div>

    <div class="voucher">
      <div class="amount-row">
        <div>
          <div class="lbl">${t("Amount Paid", "المبلغ المدفوع")}</div>
          <div class="words">${amountInWords(expense.amount, isAR)}</div>
        </div>
        <div class="amt">${fmt(expense.amount)}</div>
      </div>

      <div class="grid">
        <div class="cell">
          <div class="k">${t("Category", "الفئة")}</div>
          <div class="v">${escape(expense.category || "—")}</div>
        </div>
        <div class="cell">
          <div class="k">${t("Type", "النوع")}</div>
          <div class="v">${expense.expense_type === "fixed" ? t("Fixed", "ثابت") : t("Variable", "متغيّر")}</div>
        </div>
        <div class="cell">
          <div class="k">${t("Paid From", "مدفوع من")}</div>
          <div class="v">${escape(account?.name || (expense.account_id ? t("Account", "حساب") + " " + (expense.account_id || "").slice(-6) : t("Cash on hand", "نقد يدوي")))}</div>
        </div>
        <div class="cell">
          <div class="k">${t("Recurring", "متكرر")}</div>
          <div class="v">${expense.recurring ? t("Yes — " + (expense.frequency || ""), "نعم — " + (expense.frequency || "")) : t("No", "لا")}</div>
        </div>
        ${expense.recurring && expense.start_date ? `
        <div class="cell">
          <div class="k">${t("Start Date", "تاريخ البدء")}</div>
          <div class="v">${escape(expense.start_date)}</div>
        </div>` : ""}
        ${expense.recurring && expense.end_date ? `
        <div class="cell">
          <div class="k">${t("End Date", "تاريخ الانتهاء")}</div>
          <div class="v">${escape(expense.end_date)}</div>
        </div>` : ""}
      </div>
    </div>

    <div class="desc-box">
      <h3>${t("Description / Purpose", "البيان / الغرض")}</h3>
      <div class="v">${escape(expense.description || "—")}</div>
    </div>

    <div class="sig-grid">
      <div class="sig-box">${t("Received by", "استلمها")}</div>
      <div class="sig-box">${t("Prepared by", "أعدّها")}</div>
      <div class="sig-box">${t("Approved by", "اعتمدها")}</div>
    </div>

    <div class="footer">
      ${t("This is an internal accounting voucher.", "هذا سند محاسبي داخلي.")}
    </div>

    <div class="actions">
      <button onclick="window.print()">${t("Print", "طباعة")}</button>
    </div>
  </div>
  <script>setTimeout(function(){ window.print(); }, 400);</script>
</body>
</html>
  `;

  const w = window.open("", "_blank", "width=900,height=1200");
  if (!w) return;
  const blob = new Blob([html], { type: "text/html;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  w.location.href = url;
  setTimeout(() => { try { URL.revokeObjectURL(url); } catch (e) { /* noop */ } }, 60_000);
}
